/**
 * Unified File Upload Dropzone Component
 * 
 * A common drag & drop file upload component that replaces both DropFileInput and SvgFileInput.
 * Supports various file types, validation, and flexible styling.
 * 
 * Usage examples:
 * 
 * For image uploads (jpg, png):
 * <FileUploadDropzone
 *   label="Logo Image"
 *   onDrop={handleFiles}
 *   onClear={handleClear}
 *   selectedFile={file}
 *   accept={{ 'image/jpeg': [], 'image/png': [] }}
 * />
 * 
 * For SVG uploads:
 * <FileUploadDropzone
 *   label="SVG Logo"
 *   onDrop={handleFiles}
 *   onClear={handleClear}
 *   selectedFile={file}
 *   accept={{ 'image/svg+xml': [] }}
 *   maxSize={3 * 1024 * 1024}
 * />
 */

import React, { useMemo } from 'react';
import PropTypes, { InferProps } from 'prop-types';

import classnames from 'classnames/bind';
import { FileWithPath, useDropzone } from 'react-dropzone';

import COLOR from '@CONSTANTS/COLOR.constant';
import DESIGN from '@CONSTANTS/DESIGN.constant';

import styles from './FileUploadDropzone.module.scss';

const cx: CX = classnames.bind(styles);

const baseStyle = {
    cursor: 'pointer',
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    padding: '24px 20px',
    borderWidth: 1,
    borderRadius: DESIGN['main-border-radius'],
    borderColor: COLOR['marriott-light-grey'],
    backgroundColor: COLOR['marriott-light-grey-2'],
    borderStyle: 'dashed',
    outline: 'none',
    transition: 'border .24s ease-in-out, background-color .24s ease-in-out',
    minHeight: '140px',
    justifyContent: 'center',
};

const focusedStyle = {
    borderColor: COLOR['marriott-primary'],
};

const acceptStyle = {
    borderColor: '#00e676',
};

const rejectStyle = {
    borderColor: COLOR['marriott-orange'],
};

interface FileUploadDropzoneProps {
    label?: string;
    title?: string;
    onDrop: (files: File[]) => void;
    onClear?: () => void;
    selectedFile?: File | null;
    accept?: Record<string, string[]>;
    maxSize?: number;
    required?: boolean;
    placeholder?: string;
    helpText?: string;
    multiple?: boolean;
    variant?: 'default' | 'compact';
}

function FileUploadDropzone(props: FileUploadDropzoneProps) {
    const {
        label,
        title,
        onDrop,
        onClear,
        selectedFile,
        accept = {
            'image/jpeg': [],
            'image/png': [],
            'image/svg+xml': [],
        },
        maxSize = 3 * 1024 * 1024, // 3MB default
        required = false,
        placeholder,
        helpText,
        multiple = false,
        variant = 'default',
    } = props;

    const {
        getRootProps,
        getInputProps,
        isFocused,
        isDragAccept,
        isDragReject,
        fileRejections,
    } = useDropzone({
        multiple,
        accept,
        maxSize,
        onDrop: (acceptedFiles: Array<FileWithPath>) => {
            onDrop(acceptedFiles);
        },
    });

    const style: any = useMemo(() => ({
        ...baseStyle,
        ...(isFocused ? focusedStyle : {}),
        ...(isDragAccept ? acceptStyle : {}),
        ...(isDragReject ? rejectStyle : {}),
        ...(selectedFile ? { 
            borderColor: COLOR['marriott-primary'], 
            backgroundColor: '#f8fff8',
            borderStyle: 'solid',
        } : {}),
    }), [
        isFocused,
        isDragAccept,
        isDragReject,
        selectedFile,
    ]);

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / (k ** i)).toFixed(2))} ${sizes[i]}`;
    };

    const getFileTypeLabel = (file: File) => {
        if (file.type.includes('svg')) return 'SVG Image';
        if (file.type.includes('image')) return 'Image';
        return file.type || 'File';
    };

    const getAcceptedTypesText = () => {
        const types = Object.keys(accept);
        if (types.includes('image/svg+xml') && types.length === 1) {
            return 'SVG files only';
        }
        if (types.every((type) => type.startsWith('image/'))) {
            return 'Image files only';
        }
        return 'Supported file types';
    };

    const hasErrors = fileRejections.length > 0;

    return (
        <div className={cx('file-upload-dropzone', { [`variant-${variant}`]: variant !== 'default' })}>
            {label && (
                <div className={cx('field-label')}>
                    {label}
                    {required && <span className={cx('required')}>*</span>}
                </div>
            )}

            <div
                {...getRootProps({ style })}
                className={cx('dropzone', { 
                    'has-file': selectedFile,
                    'has-error': hasErrors,
                })}
            >
                <input {...getInputProps()} />
                
                {selectedFile ? (
                    <div className={cx('file-info')}>
                        <div className={cx('file-details')}>
                            <div className={cx('file-name')}>{selectedFile.name}</div>
                            <div className={cx('file-size')}>{formatFileSize(selectedFile.size)}</div>
                            <div className={cx('file-type')}>{getFileTypeLabel(selectedFile)}</div>
                        </div>
                        <div className={cx('file-actions')}>
                            {onClear && (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onClear();
                                    }}
                                    className={cx('clear-button')}
                                    title="Remove file"
                                >
                                    ×
                                </button>
                            )}
                            <div className={cx('change-hint')}>Click or drop to change</div>
                        </div>
                    </div>
                ) : (
                    <div className={cx('upload-content')}>
                        <div className={cx('upload-icon')}>+</div>
                        <div className={cx('upload-text')}>
                            <div className={cx('primary-text')}>
                                {title || placeholder || 'Click or drag files to upload'}
                            </div>
                            <div className={cx('secondary-text')}>
                                {helpText || `${getAcceptedTypesText()}, max ${formatFileSize(maxSize)}`}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {hasErrors && (
                <div className={cx('error-messages')}>
                    {fileRejections.map(({ file, errors }) => (
                        <div key={file.name} className={cx('error-message')}>
                            {errors.map((error) => (
                                <span key={error.code}>{error.message}</span>
                            ))}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

FileUploadDropzone.propTypes = {
    label: PropTypes.string,
    title: PropTypes.string,
    onDrop: PropTypes.func.isRequired,
    onClear: PropTypes.func,
    selectedFile: PropTypes.instanceOf(File),
    accept: PropTypes.shape({}),
    maxSize: PropTypes.number,
    required: PropTypes.bool,
    placeholder: PropTypes.string,
    helpText: PropTypes.string,
    multiple: PropTypes.bool,
    variant: PropTypes.oneOf(['default', 'compact']),
};

FileUploadDropzone.defaultProps = {
    label: undefined,
    title: undefined,
    onClear: undefined,
    selectedFile: null,
    accept: {
        'image/jpeg': [],
        'image/png': [],
        'image/svg+xml': [],
    },
    maxSize: 3 * 1024 * 1024,
    required: false,
    placeholder: undefined,
    helpText: undefined,
    multiple: false,
    variant: 'default',
};

export default FileUploadDropzone; 